import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faRoute, faClock, faGasPump, faSackDollar, faChevronDown } from "@fortawesome/free-solid-svg-icons"
import { DeliveryRoutePlan } from "../../../Objects/DeliveryEntity/DeliveryRoutePlan"
import { CourierRouteStopMap } from "./CourierRouteStopMap"

interface Props {
  plan: DeliveryRoutePlan
  selectedOptionKey?: string
  onSelectOption: (key: string) => void
  expandedStopKey?: string
  onToggleStop: (key: string) => void
}

export const getRouteStopKey = (stop: { deliveryId: number; stopType: number }) =>
  `${stop.deliveryId}-${stop.stopType}`

async function copyToClipboard(value: string) {
  if (!value) {
    return
  }

  try {
    await navigator.clipboard.writeText(value)
  } catch {
    const textArea = document.createElement("textarea")
    textArea.value = value
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand("copy")
    document.body.removeChild(textArea)
  }
}

interface StatTileProps {
  icon: typeof faRoute
  label: string
  value: string
  accentClassName?: string
}

function StatTile({ icon, label, value, accentClassName }: StatTileProps) {
  return (
    <div className="rounded-xl bg-white p-3 text-center shadow-sm ring-1 ring-slate-100">
      <FontAwesomeIcon icon={icon} className="text-slate-400" />

      <p className={`mt-1 text-base font-black ${accentClassName ?? "text-slate-800"}`}>{value}</p>

      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    </div>
  )
}

export function CourierRoutePlanBody({
  plan,
  selectedOptionKey,
  onSelectOption,
  expandedStopKey,
  onToggleStop,
}: Props) {
  const { t } = useTranslation("frontend")

  const selectedOption = plan.options.find((option) => option.key === selectedOptionKey) ?? plan.options[0]

  if (!selectedOption) {
    return null
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 rounded-xl bg-white p-1.5 shadow-sm ring-1 ring-slate-200">
        {plan.options.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => onSelectOption(option.key)}
            className={`rounded-lg py-2.5 text-xs font-bold transition ${
              selectedOptionKey === option.key
                ? "bg-gradient-to-r from-orange-500 to-red-500 text-white shadow"
                : "text-slate-600 hover:bg-slate-50"
            }`}
            data-interaction-description-key="interactionDescriptions:pages.delivery.components.courierRoutePlanBody.button.selectOption"
          >
            {option.name}

            {option.isRecommended && (
              <span
                className={`ml-1 rounded-full px-1.5 py-0.5 text-[9px] ${
                  selectedOptionKey === option.key ? "bg-white/25 text-white" : "bg-emerald-500 text-white"
                }`}
              >
                {t("route_plan.recommended")}
              </span>
            )}
          </button>
        ))}
      </div>

      <p className="rounded-lg bg-white px-3 py-2 text-xs leading-relaxed text-slate-600 shadow-sm ring-1 ring-slate-100">
        {selectedOption.description}
      </p>

      <div className="grid grid-cols-2 gap-2">
        <StatTile icon={faRoute} label={t("route_plan.distance")} value={`${selectedOption.totalDistanceKm.toFixed(1)} km`} />

        <StatTile
          icon={faClock}
          label={t("route_plan.duration")}
          value={`${selectedOption.estimatedDurationMinutes} min`}
        />

        <StatTile
          icon={faGasPump}
          label={t("route_plan.fuel_cost")}
          value={selectedOption.estimatedFuelCost.toMoney()}
        />

        <StatTile
          icon={faSackDollar}
          label={t("route_plan.earnings")}
          value={selectedOption.estimatedEarnings.toMoney()}
          accentClassName="text-emerald-600"
        />
      </div>

      <div className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-100">
        <h2 className="mb-3 text-xs font-black uppercase tracking-wide text-slate-500">
          {t("route_plan.stops_title")}
        </h2>

        <div className="space-y-1">
          {selectedOption.stops.map((stop, index) => {
            const stopKey = getRouteStopKey(stop)
            const isExpanded = expandedStopKey === stopKey

            return (
              <div key={stopKey} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-black text-white ${
                      stop.isPickup() ? "bg-blue-600" : "bg-emerald-600"
                    }`}
                  >
                    {stop.sequence}
                  </span>

                  {index < selectedOption.stops.length - 1 && <span className="my-1 w-0.5 flex-1 bg-slate-200" />}
                </div>

                <div className="min-w-0 flex-1 pb-4">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => onToggleStop(stopKey)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        onToggleStop(stopKey)
                      }
                    }}
                    className="cursor-pointer select-none"
                    data-interaction-description-key="interactionDescriptions:pages.delivery.components.courierRoutePlanBody.button.toggleStopMap"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-bold text-slate-800">
                        {stop.isPickup() ? t("route_plan.stop_pickup") : t("route_plan.stop_dropoff")}
                        {stop.customerName ? ` • ${stop.customerName}` : ""}
                      </p>

                      {stop.distanceFromPreviousKm > 0 && (
                        <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">
                          +{stop.distanceFromPreviousKm.toFixed(1)} km
                        </span>
                      )}
                    </div>

                    <div className="mt-0.5 flex items-center justify-between gap-2">
                      <p className="truncate text-xs text-slate-500">{stop.address}</p>

                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            copyToClipboard(stop.address)
                          }}
                          className="rounded px-2 py-1.5 text-[11px] font-semibold text-blue-600 hover:underline"
                          data-interaction-description-key="interactionDescriptions:pages.delivery.components.courierRoutePlanBody.button.copyStopAddress"
                        >
                          {t("courier_card.copy")}
                        </button>

                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className={`text-[11px] text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                        />
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-2">
                      <CourierRouteStopMap
                        latitude={stop.latitude}
                        longitude={stop.longitude}
                        stopType={stop.stopType}
                        label={stop.address}
                      />
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
